// Angular
import { Injectable, OnDestroy, OnInit } from '@angular/core';

// Libraries
import { getTimezoneOffset } from 'date-fns-tz';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { addMinutes } from 'date-fns';
import { Observable, Subject } from 'rxjs';

// Models
import { AppCleanup } from '../../../shared/models';

@Injectable({
	providedIn: 'root',
})
export class TimezoneService extends AppCleanup implements OnInit, OnDestroy {
	public timeZoneChange: TimezoneChange;
	private _timezoneChange = new Subject<TimezoneChange>();

	constructor() {
		super();
	}

	ngOnInit() {}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	getDefaultTimezones(): Timezone[] {
		return [
			{ name: 'Eastern Time', offset: this.getOffset('America/New_York'), timezoneName: 'America/New_York' },
			{ name: 'Central Time', offset: this.getOffset('America/Chicago'), timezoneName: 'America/Chicago' },
			{ name: 'Mountain Time', offset: this.getOffset('America/Denver'), timezoneName: 'America/Denver' },
			{
				name: 'Pacific Time',
				offset: this.getOffset('America/Los_Angeles'),
				timezoneName: 'America/Los_Angeles',
			},
		];
	}

	getCurrentTimeZone(): number {
		if (!Intl.DateTimeFormat().resolvedOptions().timeZone) {
			return getTimezoneOffset('America/Chicago') / 3.6e6;
		}

		return getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone) / 3.6e6;
	}

	private getOffset(timezoneName: string) {
		return getTimezoneOffset(timezoneName) / 3.6e6;
	}

	timeZoneDate(date: Date, dateTimeOffset: number): Date | null {
		if (!date) {
			return null;
		}

		let timeZone = null;

		// Should only be null if something is wrong with user's clock. This is happening in Odessa.
		if (dateTimeOffset == null) dateTimeOffset = -5;

		let defaultTimeZone = this.getDefaultTimezones().find((t) => t.offset === dateTimeOffset);

		if (!defaultTimeZone) {
			// Final stop-gap, should never reach here, unless some how the dateTimeOffset was not valid
			dateTimeOffset = -5;
			defaultTimeZone = this.getDefaultTimezones().find((t) => t.offset === dateTimeOffset);
		}

		const timeZoneName = defaultTimeZone.timezoneName;

		timeZone = toZonedTime(date, timeZoneName);

		return timeZone;
	}

	utcDate(date: Date, dateTimeOffset: number): Date | null {
		if (!date) {
			return;
		}

		let timeZone = null;

		if (dateTimeOffset != null) {
			const timeZoneName = this.getDefaultTimezones().find((t) => t.offset === dateTimeOffset)?.timezoneName;
			timeZone = fromZonedTime(date, timeZoneName);
		} else {
			timeZone = fromZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone);
		}

		return timeZone;
	}

	changeTimezone(change: TimezoneChange) {
		this.timeZoneChange = change;
		this._timezoneChange.next(change);
	}

	onTimezoneChanged(): Observable<TimezoneChange> {
		return this._timezoneChange.asObservable();
	}
	
	convertDateToJobTimezone(timestampOrDate: any, jobOffset: number): Date {
		// This function converts date passed to job timezone
		let date: Date = new Date(timestampOrDate);
		
		// Apply job offset in minutes
		date = addMinutes(date, jobOffset * 60);
		let localOffset = -(date.getTimezoneOffset() * 60000);
		let jobSiteOffset = jobOffset * 60000 * 60;
		
		return new Date(date.getTime() - (localOffset - jobSiteOffset));
	}

	getOffsetFromJobTimezone(timestampOrDate: any, jobOffset: number): number {
		// This function returns offset between date passed and job timezone
		let date: Date = new Date(timestampOrDate);
		
		// Apply job offset in minutes
		date = addMinutes(date, jobOffset * 60);
		let localOffset = -date.getTimezoneOffset();
		let jobSiteOffset = jobOffset * 60;
		
		return (localOffset - jobSiteOffset) / 60;
	}
	
	getJobOffset(utcDateTimeOffset: number | null | undefined): number {
		if (this.isValidUtcOffset(utcDateTimeOffset)) {
			return utcDateTimeOffset;
		}
		
		// Fall back to current Central Time offset, if the job offset is not set which is not uncommon.
		const offsetMs = getTimezoneOffset('America/Chicago', new Date());
		return offsetMs / (1000 * 60 * 60);
	}
	
	isValidUtcOffset(offset: number | null | undefined): boolean {
		return (
			typeof offset === 'number' &&
			Number.isFinite(offset) &&
			offset >= -12 &&
			offset <= 14
		);
	}
	
	offsetToTimezoneString(offset: number): string {
		const sign = offset >= 0 ? '+' : '-';
		const abs = Math.abs(offset);
		const hours = Math.floor(abs).toString().padStart(2, '0');
		const minutes = ((abs % 1) * 60).toString().padStart(2, '0');
		return `${sign}${hours}:${minutes}`;
	}
}

export interface Timezone {
	name: string;
	offset: number;
	timezoneName: string;
}

export interface TimezoneChange {
	changed: boolean;
	offset: number;
}
